import type { Boss, CampaignState, Difficulty, GameState, Player, ScreenShake } from './types'
import { ARENA, BODY_EDGE_BASELINE_HALF_W, EXECUTE, PLAYER } from './balance'
import { BOSS_DEFINITIONS, type BossDefinition, getBossDefinition } from './bosses'
import { createCampaign, createScratchCampaign } from './campaign'
import { initBossSpriteRuntime } from './sprites'
import { econ } from './econ'
import { econBaseShape } from './econ_baseline'
import { playerMaxHp, setDifficulty } from './difficulty'
import { loadSettings, isSenshurakuOpen } from './persistence'
import { TIPS_CARD_HOLD_MS } from './tutorial_cards'
import { MENU_NAV_INITIAL } from './menu_nav'
import { createExecutionerRuntime, isExecutioner } from './executioner_weapon'

export function createPlayer(): Player {
  return {
    x: 280,
    y: ARENA.floorY,
    facing: 'right',
    hp: playerMaxHp(),   // 初演=×2 / 千秋楽=base。 difficulty モジュールの current を参照。
    posture: 0,
    state: 'idle',
    stateElapsed: 0,
    attackHitConsumed: false,
    attackContactKind: null,
    counterWindow: 0,
    attackIsCounter: false,
    parryAbsorbed: false,
    executeHitIndex: 0,
    executeVariant: 'normal',
    hurtVisualUntil: 0,
    hurtNonce: 0,
    parryChain: 0,
    lastParryTime: null,
    lastParryPressMs: -9999,
    lastStepStartMs: -9999,
    parryMashCount: 0,
    parryActiveMsEffective: PLAYER.parryActiveMs,
    postureRegenLockSec: 0,
  }
}

export function createBossFromDefinition(def: BossDefinition): Boss {
  const x = 680
  // 経済プリセット v5 (docs/reports/2026-07-03-hp-posture-economy-tuning.md §8):
  //   bossPools[def.id] で HP/体幹/部屋配分を上書き。 指定フィールドだけ上書き・
  //   未指定/未登録ボスは def のまま (v1 は bossPools 自体が undefined=常に def のまま)。
  const pool = econ().bossPools?.[def.id]
  const roomHpFractions = pool?.roomHpFractions ?? def.roomHpFractions
  // 体幹と体力の基準の比較版 (DEV ?econBase=1|2・src/econ_baseline.ts)。切れている時 (配布版) は値がそのまま通り、actMaxPostures は undefined。
  const econBase = econBaseShape(def.id, def.roomCount ?? 1, pool?.maxHp ?? def.maxHp, pool?.maxPosture ?? def.maxPosture)
  const maxHp = econBase.maxHp
  const actMaxPostures = econBase.actMaxPostures
  const maxPosture = actMaxPostures?.[0] ?? (pool?.maxPosture ?? def.maxPosture)
  const finalRoomPostureMult = pool?.finalRoomPostureMult ?? def.finalRoomPostureMult
  // 本物のSEKIRO型 (2026-07-15移行): 各段階=独立フルHPバーなので、開始HPも第0段階の
  //   バーサイズに揃える (roomFractions/phaseFullHp と同じ正規化式・combat.ts と対応)。
  const roomCount = Math.max(1, def.roomCount ?? 1)
  const startFracs = (roomHpFractions && roomHpFractions.length === roomCount)
    ? roomHpFractions : Array(roomCount).fill(1 / roomCount)
  const startFracSum = startFracs.reduce((a, b) => a + b, 0) || 1
  const startHp = Math.round(maxHp * (startFracs[0]! / startFracSum))
  return {
    id: def.id,
    displayName: def.name,
    x,
    y: ARENA.floorY,
    facing: 'left',
    hp: startHp,
    maxHp,
    posture: 0,
    maxPosture,
    postureRegenLockSec: 0,
    state: 'idle',
    stateElapsed: 0,
    currentAttack: null,
    attackQueue: [],
    actionAnchorX: x,
    lungeTargetX: x,
    attackCount: 0,
    comboHitIndex: 0,
    lastAttackStreak: 0,
    finisherAnchorOffset: def.finisherAnchorOffset ?? EXECUTE.defaultFinisherAnchor,
    bodyHalfWidthPx: def.bodyHalfWidthPx ?? BODY_EDGE_BASELINE_HALF_W,
    executesTaken: 0,
    phaseTransitionTriggered: false,
    roomEntryForcedDone: [],
    blinkCooldownMs: 0,
    blinkFollowupPending: false,
    recentHitAgesMs: [],
    retaliationCooldownMs: 0,
    behindMs: 0,
    proximityHeldMs: 0,
    greedHitCount: 0,
    postExecuteInvincMs: 0,
    roomCount: def.roomCount ?? 1,
    roomHpFractions,
    finalRoomPostureMult,
    ...(actMaxPostures ? { actMaxPostures } : {}),
    protectFromDeath: def.protectFromDeath ?? false,
    meleeGuard: def.meleeGuard ?? 'none',
    contactMaterial: def.contactMaterial ?? 'flesh',
    currentString: null,
    beatIndex: 0,
    lastStringId: null,
    // 請われぬ介錯だけ刃/砲の形態状態を持つ (他ボスは undefined = 分岐に一切入らない)。
    exec: isExecutioner(def) ? createExecutionerRuntime() : undefined,
  }
}

export function noShake(): ScreenShake {
  return { remainingSec: 0, amplitude: 0, startAmplitude: 0, totalDur: 0, startSec: 0 }
}

// 新規ゲーム開始 or キャンペーン継続でstateを初期化
export function createInitialState(campaign?: CampaignState): GameState {
  const c = campaign ?? createCampaign()
  const def = getBossDefinition(c.currentBossIndex)
  // 難易度モジュールを保存値へ同期してから player を生成する
  //   (createPlayer が playerMaxHp() を読むため、 この順序が必須)。
  const settings = loadSettings()
  setDifficulty(settings.difficulty)
  return {
    phase: 'playing',
    phaseElapsedSec: 0,
    defeatRetryBuffered: false,
    time: 0,
    player: createPlayer(),
    boss: createBossFromDefinition(def),
    campaign: c,
    sfx: [],
    fx: [],
    hitStopRemaining: 0,
    shake: noShake(),
    bossSprite: initBossSpriteRuntime(),
    finisher: null,
    deathBeat: null,
    lastDeathCause: null,
    timeScale: 1,
    hits: [],
    choiceIndex: 0,
    endingChoice: null,
    titleIndex: 0,
    titleEntered: false,
    practice: false,
    solo: false,
    bossIntroMs: 0,
    // 稽古モード (tutorialLessons を持つボスのみ)。 リトライ/再演でも頭からやり直し
    tutorial: def.tutorialLessons
      // 開幕の1枚目は「間」を置かずに出す (まだ何も起きていない = 待たせる理由がない)。
      //   cardHoldMs = TIPS_CARD_HOLD_MS で「間はもう消化済み」の意味。
      ? { lesson: 0, successes: 0, cycleClean: false, cardCursor: 0, cardEnterMs: 0, cardHoldMs: TIPS_CARD_HOLD_MS }
      : null,
    // 幕間ADVのランタイム (interlude フェーズ中のみ非null)
    adv: null,
    paused: false,
    pauseIndex: 0,
    pausePanel: null,
    titleNewGame: false,
    newGameConfirmIndex: 1,
    difficultyIndex: settings.difficulty === 'hatsuen' ? 1 : 0,
    senshurakuOpen: isSenshurakuOpen(),
    settings,
    menuNav: MENU_NAV_INITIAL,
  }
}

// ===== 一戦だけモード (2026-07-28) =====
// `?fight=<bossId>` で開いた時の初期状態。 タイトルも物語も通らず、 そのボスとの戦闘だけが始まる。
//
// なぜ専用の入口が要るか: dev フック `__setBoss` は `import.meta.env.DEV` の箱の中にあり、
//   配布ビルドでは箱ごと消える (= G キーの無敵などが本番に出ないための境界)。 その箱から
//   引っ張り出して使うと境界が壊れるので、 本番に出てよい入口をここに独立して持つ。
//
// 難易度は「初演」(やさしい方) 固定。 単体リンクを渡す相手は、 この作品の作法を知らない前提。
//   ⚠️ 保存しない = 本編の設定 (localStorage) を書き換えない。 戻すのはこの定数1つ。
export const SOLO_DIFFICULTY: Difficulty = 'hatsuen'

// DEV ?soloDiff=senshuraku|hatsuen (2026-10-09 夜・main.ts の DEV の箱の中でだけ呼ぶ): 一戦だけモードの難易度を URL で選ぶ。
//   「もう一度」(game.ts restartSolo) も createSoloState を通し直すので、 ここへ持っておけば難易度が保たれる。
//   配布版は setSoloDifficulty を呼ばない = 常に SOLO_DIFFICULTY (初演) のまま。
let soloDifficulty: Difficulty = SOLO_DIFFICULTY
export function setSoloDifficulty(d: Difficulty): void {
  soloDifficulty = d
}
export function getSoloDifficulty(): Difficulty {
  return soloDifficulty
}

export function createSoloState(bossId: string): GameState | null {
  const idx = BOSS_DEFINITIONS.findIndex((b) => b.id === bossId)
  if (idx < 0) return null
  // 下書き印つきの進行 = saveCampaign が入口で弾く (2026-07-26 稽古セーブ破壊修正と同じ仕組み)。
  //   相手が本編も遊んでいた場合でも、 その進行は1ミリも動かない。
  const base = createInitialState({ ...createScratchCampaign(), currentBossIndex: idx })
  // createInitialState は保存済み設定の難易度で player を作るので、 ここで作り直す
  //   (createPlayer が playerMaxHp() を読む = setDifficulty の後でなければ HP が変わらない)。
  setDifficulty(soloDifficulty)
  return {
    ...base,
    phase: 'playing',
    solo: true,
    // 一戦の絵が読めるまで黒のまま待つ (src/battle_asset_gate.ts battleEntryStep・11-03)。
    battleEntryWaitMs: 0,
    // 稽古 (TIPSカード+課題) は通さない。 一戦だけモードは「そのボスとの戦闘」だけを見せる約束。
    tutorial: null,
    player: createPlayer(),
    settings: { ...base.settings, difficulty: soloDifficulty },
    difficultyIndex: soloDifficulty === 'hatsuen' ? 1 : 0,
  }
}

// レガシー互換: createBoss(id, name, ...) も残す (テスト用)
export { createCampaign } from './campaign'

// テスト/DEV 用: TIPS チュートリアルカードを全て既読にして、カードゲート (game.ts) を素通りさせる。
//   本編では「カードを読む→閉じる」まで戦闘が止まる。戦闘そのものを検証するテストは
//   カードを読み飛ばした状態から始める (カード表示は tutorial_flow / tips_cards のテストで検証)。
export function withTipsRead(state: GameState): GameState {
  if (!state.tutorial) return state
  // 10-11 から稽古の既定は「止めて教える」(src/tut_focus.ts 'every')。カードと同じく、止めの場面も全部飛ばす。
  return { ...state, tutorial: { ...state.tutorial, cardCursor: TIPS_ALL_READ, focusSkipped: true } }
}
export const TIPS_ALL_READ = 999

// ===== ボス名乗りカードの「間」 (2026-07-19) =====
// 問題: 名乗りカードは 3.4 秒描かれるのに戦闘シムが止まっておらず、 ボスは 0.69〜1.11 秒で
//   間合いに入って初手を振っていた (= 名乗りの最中に殴られる)。
// 方針: カードが読める間は戦闘を止める。 TIPS カード (game.ts) と同じ「表示中はシムを止める」型。
//   全 3.4 秒は長すぎる (リトライのたびに待たされる) ので、 名前が読み切れる 1.8 秒で解放し、
//   残り (2.6→3.4 のフェードアウト) は戦闘と重ねる。 攻撃ボタンでいつでもスキップできる。
export const BOSS_INTRO_HOLD_MS = 1800
// 導入戦 (名も無き亡霊) は「静かに始めたい」ので名乗りカード自体を出さない = 「間」も無し。
//   条件を render.ts と game.ts で二重に持たないよう、 ここを唯一の判定元にする。
export function bossIntroCardShown(bossId: string): boolean {
  return bossId !== 'nameless-shade'
}
export function bossIntroHolding(state: GameState): boolean {
  return state.phase === 'playing'
    && bossIntroCardShown(state.boss.id)
    && state.bossIntroMs < BOSS_INTRO_HOLD_MS
}
// テスト/DEV 用: 名乗りの「間」を消化済みにする。 戦闘そのものを検証するテストは
//   開幕の「間」を挟まず頭から動かしたいので、 これを通してから tick する (withTipsRead と同型)。
export function withBossIntroDone(state: GameState): GameState {
  return { ...state, bossIntroMs: BOSS_INTRO_HOLD_MS }
}
